import React from 'react';
import { Trash2, ExternalLink, Facebook, Instagram, MessageCircle, Phone, Info } from 'lucide-react';
import ColorSwatchPicker from './ColorSwatchPicker';

interface CTAButtonData {
  text: string;
  url: string;
  buttonColor: string;
  textColor: string;
  size: 'small' | 'medium' | 'large';
  icon?: 'none' | 'facebook' | 'instagram' | 'line' | 'phone' | 'external';
}

interface CTAButtonBlockProps {
  data: CTAButtonData;
  onChange: (data: CTAButtonData) => void;
  onDelete: () => void;
  isPreview?: boolean;
}

const CTAButtonBlock: React.FC<CTAButtonBlockProps> = ({ data, onChange, onDelete, isPreview }) => {
  const sizeClasses = {
    small: 'px-6 py-3 text-base',
    medium: 'px-10 py-4 text-xl',
    large: 'px-12 py-5 text-2xl'
  };

  const iconMap: Record<string, any> = {
    none: null,
    facebook: Facebook,
    instagram: Instagram,
    line: MessageCircle,
    phone: Phone,
    external: ExternalLink
  };

  if (isPreview) {
    const sizeClasses = {
      small: 'px-8 py-4 text-lg',
      medium: 'px-10 py-5 text-2xl',
      large: 'px-12 py-6 text-3xl',
    };

    const IconComponent = iconMap[data.icon || 'none'];

    // Create galaxy gradient from selected color
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 147, g: 51, b: 234 }; // fallback purple
    };

    const rgb = hexToRgb(data.buttonColor);
    
    // Create 3 color stops for galaxy effect: darker -> base -> lighter
    const darkerColor = `rgb(${Math.max(0, rgb.r - 40)}, ${Math.max(0, rgb.g - 40)}, ${Math.max(0, rgb.b - 40)})`;
    const baseColor = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
    const lighterColor = `rgb(${Math.min(255, rgb.r + 60)}, ${Math.min(255, rgb.g + 60)}, ${Math.min(255, rgb.b + 60)})`;

    const gradientStyle = {
      background: `linear-gradient(135deg, ${darkerColor} 0%, ${baseColor} 50%, ${lighterColor} 100%)`,
      color: data.textColor
    };

    const glowStyle = {
      background: `linear-gradient(135deg, ${darkerColor} 0%, ${baseColor} 50%, ${lighterColor} 100%)`,
    };

    return (
      <div className="flex justify-center">
        <a
          href={data.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`group relative inline-flex items-center gap-3 rounded-full font-extrabold shadow-2xl transition-all duration-500 hover:scale-110 active:scale-95 ${
            sizeClasses[data.size]
          }`}
          style={gradientStyle}
        >
          {/* Animated glow effect */}
          <div
            className="absolute inset-0 rounded-full opacity-75 blur-xl animate-pulse"
            style={glowStyle}
          />

          {/* Shimmer effect */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
          </div>

          {/* Content */}
          <div className="relative flex items-center gap-3 animate-bounce">
            {IconComponent && <IconComponent className="w-7 h-7 drop-shadow-lg" />}
            <span className="drop-shadow-lg">{data.text}</span>
          </div>

          {/* Ripple effect on hover */}
          <div className="absolute inset-0 rounded-full border-4 border-white/50 scale-100 group-hover:scale-125 opacity-100 group-hover:opacity-0 transition-all duration-700" />
        </a>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">ปุ่ม Call-to-Action</h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">ปุ่มเรียกร้องให้ลูกค้าดำเนินการ</p>
              <p className="text-gray-300">สร้างปุ่มที่โดดเด่นเพื่อให้ลูกค้ากดติดต่อ สั่งซื้อ หรือดำเนินการตามที่ต้องการ พร้อมเอฟเฟกต์แบบ Galaxy</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ข้อความบนปุ่ม</label>
          <div className="flex items-center gap-2">
            <ColorSwatchPicker
              compact
              color={data.textColor}
              fallback="#ffffff"
              onChange={(hex) => onChange({ ...data, textColor: hex })}
            />
            <input
              type="text"
              value={data.text}
              onChange={(e) => onChange({ ...data, text: e.target.value })}
              placeholder="เช่น สั่งซื้อเลย, ติดต่อเรา"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ลิงก์ URL</label>
          <input
            type="url"
            value={data.url}
            onChange={(e) => onChange({ ...data, url: e.target.value })}
            placeholder="https://..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ขนาดปุ่ม</label>
          <div className="flex items-center gap-2">
            <ColorSwatchPicker
              compact
              color={data.buttonColor}
              onChange={(hex) => onChange({ ...data, buttonColor: hex })}
            />
            <select
              value={data.size}
              onChange={(e) => onChange({ ...data, size: e.target.value as 'small' | 'medium' | 'large' })}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="small">เล็ก</option>
              <option value="medium">กลาง</option>
              <option value="large">ใหญ่</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ไอคอน</label>
          <select
            value={data.icon || 'external'}
            onChange={(e) => onChange({ ...data, icon: e.target.value as any })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="none">ไม่มีไอคอน</option>
            <option value="external">ลิงก์ภายนอก</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="line">LINE</option>
            <option value="phone">โทรศัพท์</option>
          </select>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-2">ตัวอย่าง:</p>
          <div className="flex justify-center">
            <button
              className={`${sizeClasses[data.size]} rounded-xl font-bold inline-flex items-center gap-3 shadow-2xl hover:scale-105 transition-transform`}
              style={{ backgroundColor: data.buttonColor, color: data.textColor }}
            >
              {data.text || 'ข้อความบนปุ่ม'}
              {(() => {
                const IconComponent = iconMap[data.icon || 'external'];
                return IconComponent ? <IconComponent className="w-5 h-5" /> : null;
              })()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CTAButtonBlock;
